import {MobileTab, MOBILE_MENU_TAB, MOBILE_TABS} from './mobile.constants';
import {
	getMobilePlanningUrl,
	isMobilePlanningPathAllowed,
	MOBILE_PLANNING_ME_URL,
} from './mobile-planning.util';
import {SessionService} from './session.service';

export type {MobileTab};

export const MOBILE_CONTRACTS_ME_URL = '/pages/employees/contracts/me';


/** Routes ouvertes depuis le planning dashboard (hors périmètre onglets). */
const MOBILE_DASHBOARD_ACTION_ROUTES: Array<{
	pattern: RegExp;
	hasAccess: (session: SessionService) => boolean;
}> = [
	{
		pattern: /^\/pages\/caisse\/comptage(?:\/|$)/,
		hasAccess: (session) => hasPermissionOnSelectedRestaurant(session, 'caisse'),
	},
	{
		pattern: /^\/pages\/frigos\/temperature(?:\/|$)/,
		hasAccess: (session) => hasPermissionOnSelectedRestaurant(session, 'frigo'),
	},
	{
		pattern: /^\/pages\/suivi-pain(?:\/|$)/,
		hasAccess: (session) => hasPermissionOnSelectedRestaurant(session, 'suivi_pain'),
	},
	{
		pattern: /^\/pages\/service(?:\/|$)/,
		hasAccess: (session) =>
			hasPermissionOnSelectedRestaurant(session, 'caisse')
			|| hasPermissionOnSelectedRestaurant(session, 'frigo')
			|| hasPermissionOnSelectedRestaurant(session, 'suivi_pain')
			|| hasPermissionOnSelectedRestaurant(session, 'reservations'),
	},
	{
		pattern: /^\/pages\/commandes\/\d+\/reception(?:\/|$)/,
		hasAccess: (session) => hasMobileReceptionAccess(session),
	},
	{
		pattern: /^\/pages\/devis\/detail\/\d+(?:\/|$)/,
		hasAccess: (session) =>
			hasPermissionOnSelectedRestaurant(session, 'devis_traiteur')
			|| hasPermissionOnSelectedRestaurant(session, 'devis_groupe'),
	},
];

function hasPermissionOnSelectedRestaurant(session: SessionService, permission: string): boolean {
	const restaurantId = session.getRestaurantId();
	if (!restaurantId) {
		return session.hasPermission(permission);
	}
	return session.hasPermission(permission, restaurantId);
}

function hasMobileReceptionAccess(session: SessionService): boolean {
	return (
		hasPermissionOnSelectedRestaurant(session, 'stock')
		|| hasPermissionOnSelectedRestaurant(session, 'reception_stock')
		|| hasPermissionOnSelectedRestaurant(session, 'reservations')
		|| hasPermissionOnSelectedRestaurant(session, 'restaurant')
		|| hasPermissionOnSelectedRestaurant(session, 'restaurant_full')
	);
}

function isMobileDashboardActionUrlAllowed(path: string, session: SessionService): boolean {
	const route = MOBILE_DASHBOARD_ACTION_ROUTES.find((item) => item.pattern.test(path));
	return !!route && route.hasAccess(session);
}

/** Accès aux onglets manager mobile (dashboard, réservations, planning équipe…). */
export function hasMobileManagerAccess(session: SessionService): boolean {
	return (
		hasPermissionOnSelectedRestaurant(session, 'restaurant')
		|| hasPermissionOnSelectedRestaurant(session, 'planning')
		|| hasPermissionOnSelectedRestaurant(session, 'planning_view')
		|| hasPermissionOnSelectedRestaurant(session, 'reservations')
		|| hasPermissionOnSelectedRestaurant(session, 'restaurant_full')
	);
}

/**
 * Salarié extra : `mon_planning` sur le restaurant courant, sans accès manager mobile.
 * Navigation limitée à mon planning (+ mes contrats si permission).
 */
export function isMobileEmployeeOnlyUser(session: SessionService): boolean {
	return hasPermissionOnSelectedRestaurant(session, 'mon_planning') && !hasMobileManagerAccess(session);
}

export function getMobileDefaultUrl(session: SessionService): string {
	if (isMobileEmployeeOnlyUser(session)) {
		return MOBILE_PLANNING_ME_URL;
	}
	return getMobilePlanningUrl(session);
}

export function getMobileTabs(session: SessionService): MobileTab[] {
	if (isMobileEmployeeOnlyUser(session)) {
		const tabs: MobileTab[] = [
			{
				id: 'planning',
				label: 'Mon planning',
				icon: 'pi-calendar-plus',
				route: MOBILE_PLANNING_ME_URL,
				matchPrefixes: ['/pages/planning/me'],
			},
		];

		if (hasPermissionOnSelectedRestaurant(session, 'mes_contrats')) {
			tabs.push({
				id: 'contracts',
				label: 'Mes contrats',
				icon: 'pi-file',
				route: MOBILE_CONTRACTS_ME_URL,
				matchPrefixes: ['/pages/employees/contracts/me'],
			});
		}

		return [...tabs, MOBILE_MENU_TAB];
	}

	const planningRoute = getMobilePlanningUrl(session);
	const tabs = MOBILE_TABS.map((tab) => (
		tab.id === 'planning' ? {...tab, route: planningRoute} : tab
	)).filter((tab) => {
		if (tab.id !== 'service') {
			return true;
		}
		return hasPermissionOnSelectedRestaurant(session, 'caisse')
			|| hasPermissionOnSelectedRestaurant(session, 'frigo')
			|| hasPermissionOnSelectedRestaurant(session, 'suivi_pain')
			|| hasPermissionOnSelectedRestaurant(session, 'reservations');
	});
	return [
		...tabs,
		MOBILE_MENU_TAB,
	];
}

export function isMobileUrlAllowed(url: string, session: SessionService): boolean {
	const path = url.split('?')[0].split('#')[0];

	if (!path.startsWith('/pages/')) {
		return false;
	}

	if (path.startsWith('/pages/planning') && !isMobilePlanningPathAllowed(path, session)) {
		return false;
	}

	return true;
}

export function getActiveMobileTabId(url: string, session: SessionService): MobileTab['id'] | null {
	const tabs = getMobileTabs(session);
	const path = url.split('?')[0].split('#')[0];
	if (isMobileDashboardActionUrlAllowed(path, session)) {
		if (path.startsWith('/pages/service') || path.startsWith('/pages/suivi-pain')
			|| path.startsWith('/pages/caisse') || path.startsWith('/pages/frigos')) {
			return tabs.some((tab) => tab.id === 'service') ? 'service' : null;
		}
		return tabs.some((tab) => tab.id === 'dashboard') ? 'dashboard' : null;
	}
	const tab = tabs.find((item) => (
		!item.opensSidebar && item.matchPrefixes.some((prefix) => path.startsWith(prefix))
	));
	if (tab) {
		return tab.id;
	}

	return tabs.some((item) => item.id === 'menu') ? 'menu' : null;
}
